import type { Message, PendingApproval } from "../../shared/types";

const STORAGE_PREFIX = "resident-agent";
const CONVERSATION_STALE_TTL_MS = 30 * 60 * 1000;

export function getStorageKey(agentId: string, key: string): string {
  return `${STORAGE_PREFIX}:${agentId}:${key}`;
}

interface ConversationMeta {
  conversationId: string;
  lastActivityAt: number;
}

interface StoredMessagesPayload {
  messages: Message[];
  lastActivityAt: number;
}

function getConversationMetaKey(agentId: string): string {
  return getStorageKey(agentId, "conversationMeta");
}

function getMessagesKey(agentId: string): string {
  return getStorageKey(agentId, "messages");
}

function getPendingApprovalsKey(agentId: string): string {
  return getStorageKey(agentId, "pendingApprovals");
}

export function getConversationMeta(agentId: string): ConversationMeta | null {
  try {
    const key = getConversationMetaKey(agentId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw) as ConversationMeta;
  } catch (error) {
    console.error("Failed to get conversation meta from storage:", error);
    return null;
  }
}

export function setConversationMeta(
  agentId: string,
  meta: ConversationMeta,
): void {
  try {
    const key = getConversationMetaKey(agentId);
    localStorage.setItem(key, JSON.stringify(meta));
  } catch (error) {
    console.error("Failed to save conversation meta to storage:", error);
  }
}

export function clearConversationMeta(agentId: string): void {
  try {
    const key = getConversationMetaKey(agentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Failed to clear conversation meta from storage:", error);
  }
}

export function isConversationStale(agentId: string): boolean {
  const meta = getConversationMeta(agentId);
  if (!meta) return false;
  const timeSinceLastActivity = Date.now() - meta.lastActivityAt;
  return timeSinceLastActivity >= CONVERSATION_STALE_TTL_MS;
}

export function getConversationId(agentId: string): string | null {
  if (isConversationStale(agentId)) {
    clearConversationData(agentId);
    return null;
  }
  const meta = getConversationMeta(agentId);
  return meta?.conversationId || null;
}

export function setConversationId(
  agentId: string,
  conversationId: string,
): void {
  setConversationMeta(agentId, {
    conversationId,
    lastActivityAt: Date.now(),
  });
}

export function touchConversation(agentId: string): void {
  const meta = getConversationMeta(agentId);
  if (meta) {
    setConversationMeta(agentId, {
      ...meta,
      lastActivityAt: Date.now(),
    });
  }
}

export function clearConversationId(agentId: string): void {
  clearConversationData(agentId);
}

export function getStoredMessages(agentId: string): Message[] | null {
  if (isConversationStale(agentId)) {
    clearConversationData(agentId);
    return null;
  }
  try {
    const key = getMessagesKey(agentId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const payload = JSON.parse(raw) as StoredMessagesPayload;
    return Array.isArray(payload?.messages) ? payload.messages : null;
  } catch (error) {
    console.warn("Failed to read conversation messages from storage:", error);
    return null;
  }
}

export function setStoredMessages(agentId: string, messages: Message[]): void {
  try {
    const key = getMessagesKey(agentId);
    const payload: StoredMessagesPayload = {
      messages,
      lastActivityAt: Date.now(),
    };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (error) {
    console.warn("Failed to persist conversation messages to storage:", error);
  }
}

export function getPendingApprovals(
  agentId: string,
): (PendingApproval & { error?: string })[] {
  try {
    const key = getPendingApprovalsKey(agentId);
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Failed to read pending approvals from storage:", error);
    return [];
  }
}

export function setPendingApprovals(
  agentId: string,
  approvals: (PendingApproval & { error?: string })[],
): void {
  try {
    const key = getPendingApprovalsKey(agentId);
    localStorage.setItem(key, JSON.stringify(approvals));
  } catch (error) {
    console.warn("Failed to persist pending approvals to storage:", error);
  }
}

export function clearPendingApprovals(agentId: string): void {
  try {
    const key = getPendingApprovalsKey(agentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Failed to clear pending approvals from storage:", error);
  }
}

export function clearConversationData(agentId: string): void {
  clearConversationMeta(agentId);
  clearPendingApprovals(agentId);
  try {
    const key = getMessagesKey(agentId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error("Failed to clear conversation data from storage:", error);
  }
}
